import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '@/components/layout';
import { Card, CardContent, TableSkeleton } from '@/components/ui';
import { ApiErrorDisplay } from '@/components/features/error';
import { useAuditEvents } from '@/hooks/use-audit-events';
import type { AuditQueryParams } from '@/types/audit';
import { AuditFilterBar } from './components/audit-filter-bar';
import { AuditStatusBar } from './components/audit-status-bar';
import { AuditTable } from './components/audit-table';
import { AuditExport } from './components/audit-export';

/** Heutiger Kalendertag in UTC (YYYY-MM-DD) — deckt sich mit dem Backend-`date`-Partition-Key. */
function todayUtc(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Audit-Trail — Plattform-Sicht (Spec 528 Phase 6, ueberarbeitet in Spec 530).
 *
 * Platform-only (Route-Gate ROLE_GROUPS.AUDIT_READ = platform_admin + platform_ops).
 * Die Seite oeffnet mit den heutigen Events (Default „heute", Spec 530 P1) statt
 * einem leeren Prompt. Orchestriert die drei Zonen: Status-Leiste (Zone A),
 * Filter-Leiste (Zone B) und Ergebnis-Tabelle (Zone C). Der Export (JSON + CSV,
 * vollstaendig paginiert) ist auf platform_admin gegated (ROLE_GROUPS.AUDIT_EXPORT).
 */
export function AuditPage() {
  const { t } = useTranslation('audit');

  // Default: heutige Events sofort laden (kein leerer Prompt mehr).
  const [query, setQuery] = useState<AuditQueryParams>(() => ({ mode: 'date', date: todayUtc() }));

  const { data, isLoading, error, isFetching } = useAuditEvents(query);
  const events = data?.events ?? [];
  const truncated = data?.cursor != null;
  const deniedActive = query.filters?.result === 'denied';

  /** Denied-Umschalter (Zone A): setzt/loescht `result=denied` serverseitig und laedt neu. */
  const toggleDenied = () => {
    setQuery((prev) => {
      const filters: NonNullable<AuditQueryParams['filters']> = { ...prev.filters };
      if (filters.result === 'denied') delete filters.result;
      else filters.result = 'denied';
      const hasFilters = Boolean(filters.action || filters.result);
      return { ...prev, filters: hasFilters ? filters : undefined };
    });
  };

  return (
    <div>
      <PageHeader title={t('title')} description={t('subtitle')} />

      {!error && data && (
        <AuditStatusBar
          events={events}
          truncated={truncated}
          deniedActive={deniedActive}
          onToggleDenied={toggleDenied}
        />
      )}

      <AuditFilterBar query={query} onSearch={setQuery}>
        <AuditExport query={query} />
      </AuditFilterBar>

      {isLoading || isFetching ? (
        <TableSkeleton />
      ) : error ? (
        <ApiErrorDisplay error={error} />
      ) : events.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">{t('empty.title')}</CardContent>
        </Card>
      ) : (
        <AuditTable events={events} />
      )}
    </div>
  );
}
